import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/system';
import Paper from '@mui/material/Paper';
import { Typography } from '@components/Typography';
import usePermissions from '@hooks/usePermissions';
import { dashboardActions } from '@actions';
import { Recommendation } from '@constants/dashboard';
import { ErrorIcon } from 'assets/images/icons';
import { useRecommendationsMap } from './recommendationsMap';

const ERROR_TYPES = [
    Recommendation.IntegrationDisconnected,
    Recommendation.ChannelDisconnected,
    Recommendation.AutomationFailed,
    Recommendation.OneTimeCampaignFailed,
];

const Stack = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    marginBottom: 16,
});

const Card = styled(Paper)({
    backgroundColor: '#FBF2F4',
    borderRadius: 8,
    padding: 24,
    '@media (max-width: 1024px)': {
        padding: 16,
    },
    display: 'grid',
    gridTemplateColumns: '24px 1fr',
    gridGap: 16,
    '& > svg': {
        width: 24,
        height: 24,
    },
    '& > svg path': {
        fill: 'var(--error-main)',
        fillOpacity: 1,
    },
});

const Cta = styled('button')({
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: 'var(--error-main)',
    font: 'inherit',
});

const CriticalAlerts = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const recommendations = useSelector(state => state.dashboard.recommendations);
    const { hasAccess, hasFullSettings, hasSettingsAccess } = usePermissions();
    const map = useRecommendationsMap();

    const items = recommendations
        .filter(r => ERROR_TYPES.includes(r.type) && !r.isHidden)
        .filter(r => {
            if (!hasFullSettings() && r.type === Recommendation.ChannelDisconnected) {
                return false;
            }
            if (
                !hasAccess('campaigns') &&
                (r.type === Recommendation.AutomationFailed || r.type === Recommendation.OneTimeCampaignFailed)
            ) {
                return false;
            }
            if (!hasSettingsAccess() && r.type === Recommendation.IntegrationDisconnected) {
                return false;
            }
            return true;
        });

    if (items.length === 0) return null;

    const onClick = (type, url) => {
        navigate(url);
        dispatch(dashboardActions.updateRecommendation(type, { isActual: false }));
    };

    return (
        <Stack>
            {items.map(item => {
                const config = map[item.type];
                if (!config) return null;
                return (
                    <Card key={item.type}>
                        <ErrorIcon />
                        <div>
                            <Typography variant="subtitle2" sx={{ color: 'var(--error-main)' }}>
                                {config.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'var(--text-secondary)', marginTop: '8px' }}>
                                {config.text}
                            </Typography>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                                <Cta type="button" onClick={() => onClick(item.type, config.url)}>
                                    <Typography variant="subtitle2" sx={{ color: 'var(--error-main)' }}>
                                        {config.urlText}
                                    </Typography>
                                </Cta>
                            </div>
                        </div>
                    </Card>
                );
            })}
        </Stack>
    );
};

export default CriticalAlerts;
